import { Button } from '@bb/ui';
import type { CanvasDefinition, CanvasStepStats } from '../../../api/client.ts';
import { exitReasonLabel, formatSeconds, orderSteps, portLabel } from './labels.ts';

const count = (n: number) => n.toLocaleString('en-US');

function Breakdown({ label, items }: { label: string; items: Array<[string, number]> }) {
  if (items.length === 0) return <>0</>;
  return (
    <ul className="step-stats__breakdown" aria-label={label}>
      {items.map(([name, n]) => (
        <li key={name}>
          {name} <strong>{count(n)}</strong>
        </li>
      ))}
    </ul>
  );
}

export interface StepStatsTableProps {
  steps: CanvasStepStats[];
  graph: Pick<CanvasDefinition, 'variants' | 'steps'> | null;
  typeLabel: (type: string) => string;
  /** Message steps whose push performance can be opened, and how (by `stepId`). */
  push?: { stepIds: ReadonlySet<string>; open: (stepId: string) => void };
}

/**
 * Per-step numbers of a Canvas (BB-123), in journey order: entered, waiting now, advanced by port (paths groups by
 * name, «Everyone Else»), exited by reason, failed, sends and time in step. Steps that are no longer in the graph
 * (removed in a later version) stay at the end, marked. A push-sending Message step opens its push performance.
 */
export function StepStatsTable({ steps, graph, typeLabel, push }: StepStatsTableProps) {
  const rows = orderSteps(graph, steps);
  return (
    <div className="push-table-scroll">
      <table className="table step-stats" aria-label="Step statistics">
        <thead>
          <tr>
            <th scope="col">Step</th>
            <th scope="col">Entered</th>
            <th scope="col" title="Entries in the step right now, whatever the period">
              Waiting now
            </th>
            <th scope="col">Advanced</th>
            <th scope="col">Exited</th>
            <th scope="col">Failed</th>
            <th scope="col" title="Messages handed to a channel by the step">
              Sends
            </th>
            <th scope="col" title="From arriving to leaving, for entries that left in the period">
              Time in step (p50 / p90)
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const name = s.name;
            return (
              <tr key={s.stepId}>
                <th scope="row">
                  {name}
                  <span className="hint"> · {typeLabel(s.type)}</span>
                  {!s.inGraph && <span className="tag tag--neutral">Not in the current graph</span>}
                </th>
                <td>{count(s.entered)}</td>
                <td>{count(s.waiting)}</td>
                <td>
                  <Breakdown label={`Advanced from ${name}`} items={Object.entries(s.advanced).map(([port, n]) => [portLabel(graph, s.stepId, port), n])} />
                </td>
                <td>
                  <Breakdown label={`Exited from ${name}`} items={Object.entries(s.exited).map(([reason, n]) => [exitReasonLabel(reason), n])} />
                </td>
                <td>{count(s.failed)}</td>
                <td>
                  {s.type === 'message' || s.sends ? count(s.sends) : '—'}
                  {push?.stepIds.has(s.stepId) && (
                    <>
                      {' '}
                      <Button size="sm" variant="ghost" aria-label={`Push performance of ${name}`} onClick={() => push.open(s.stepId)}>
                        Push
                      </Button>
                    </>
                  )}
                </td>
                <td>
                  {formatSeconds(s.timeInStep.p50Seconds)} / {formatSeconds(s.timeInStep.p90Seconds)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
